import { api } from "@repo/convex/api";
import type { Id } from "@repo/convex/dataModel";
import {
  buildEntityPath,
  type CompiledFrameworkStructure,
  type FolderRouteScope,
  getEntityParentBindingsFromStructure,
  getManagedProjectTreeFolderSystemKey,
  getRequiredEntityAlias,
  resolveActionScope,
  resolveCreateDefaults,
  rootFolderSupportsProjectTreeFolders,
  stripCollectionSlugFromFolderScope,
} from "@repo/shared";
import { useConvex } from "convex/react";
import { useCallback, useMemo } from "react";
import { useCurrentUser } from "@/hooks";
import { useObservedMutation } from "@/hooks/use-observed-convex";
import { trackEntityEvent } from "@/lib/analytics/domain";
import type { AnalyticsSurface } from "@/lib/analytics/events";
import {
  buildCreateParentScopeValues,
  type CreateActionParentEntity,
  type ResolvedCreateAction,
} from "../lib/create-actions";
import {
  compactDraftSnapshotFields,
  hasDraftSnapshotFieldValue,
} from "../lib/framework-entity-draft-snapshot";
import {
  buildEntityDisplayMap,
  getCatalogOwnerFields,
  type ProjectFrameworkCatalog,
} from "../lib/framework-registry";
import { useFrameworkEntityAliases } from "./use-framework-entity-aliases";
import { useProjectFrameworkStructure } from "./useProjectData";

export function buildInitialDraftSnapshot(input: {
  targetEntityTypeKey: string;
  catalog: ProjectFrameworkCatalog | null | undefined;
  defaults: Record<string, unknown>;
  createdEntity?: Record<string, unknown>;
  returnPath: string;
  sourceNodeKey: string;
}) {
  const snapshotFields = compactDraftSnapshotFields(input.defaults);
  for (const field of getCatalogOwnerFields(input.catalog, input.targetEntityTypeKey)) {
    const value = input.createdEntity?.[field.persistenceKey];
    if (hasDraftSnapshotFieldValue(value)) {
      snapshotFields[field.persistenceKey] = value;
    } else {
      delete snapshotFields[field.persistenceKey];
    }
  }

  return {
    ...snapshotFields,
    name: "",
    __createMeta: {
      isNew: true,
      returnPath: input.returnPath,
      sourceNodeKey: input.sourceNodeKey,
    },
  };
}

function normalizeOwnerId(value: unknown): Id<"users"> | undefined {
  return typeof value === "string" && value.length > 0 ? (value as Id<"users">) : undefined;
}

function buildProjectEntityCreatePayload(input: {
  defaults: Record<string, unknown>;
  projectTreeFolderId?: string;
  managedProjectTreeFolderSystemKey?: string | null;
}) {
  const payload: Record<string, unknown> = { ...input.defaults };

  if (input.projectTreeFolderId) {
    payload.projectTreeFolderId = input.projectTreeFolderId;
  }

  if (input.managedProjectTreeFolderSystemKey && payload.projectTreeFolderId === undefined) {
    payload.projectTreeFolderSystemKey = input.managedProjectTreeFolderSystemKey;
  }

  return payload;
}

export function buildUntitledEntityName(input: {
  action: ResolvedCreateAction["action"];
  displayLabel?: string | null;
}) {
  const displayLabel = input.displayLabel?.trim();
  if (displayLabel) {
    return `Untitled ${displayLabel}`;
  }

  const trimmedLabel = input.action.label.trim();
  if (trimmedLabel) {
    return `Untitled ${trimmedLabel}`;
  }

  return `Untitled ${input.action.targetEntityTypeKey}`;
}

function targetSupportsProjectTreeFolderBinding(
  structure: Pick<CompiledFrameworkStructure, "topologyRules">,
  targetEntityTypeKey: string
) {
  return getEntityParentBindingsFromStructure(structure, targetEntityTypeKey).some(
    (binding) => binding.parentEntityTypeKey === "projectTreeFolder"
  );
}

export function resolveProjectTreeFolderSlugFromReturnPath(input: {
  structure: Pick<CompiledFrameworkStructure, "rootFolders" | "folderNodes" | "topologyRules">;
  returnPath: string;
  targetEntityTypeKey: string;
}) {
  if (!targetSupportsProjectTreeFolderBinding(input.structure, input.targetEntityTypeKey)) {
    return undefined;
  }

  const scope = resolveActionScope(input.returnPath);
  if (
    !rootFolderSupportsProjectTreeFolders({
      structure: input.structure,
      rootFolderKeyOrSlug: scope.rootFolderSlug,
    })
  ) {
    return undefined;
  }

  return scope.folderSlugs[0];
}

/**
 * An explicitly picked project-tree folder. `projectTreeFolderId` is what the create payload
 * binds; `folderSlug` is what the returned entity path is built from, so the caller lands in the
 * folder it chose rather than the one it was standing in.
 */
export type CreateActionProjectTreeFolderOverride = {
  projectTreeFolderId: string;
  folderSlug: string;
};

/**
 * An explicitly picked parent entity for the action's `parentBinding`. The alias comes from the
 * binding itself, so callers only supply identity.
 */
export type CreateActionParentEntityOverride = {
  entityTypeKey: string;
  entityId: string;
  entitySlug?: string;
};

/**
 * Substitutes an explicitly picked parent entity for the one the navigation context resolved.
 * Refuses a parent the action cannot bind instead of silently creating the entity outside it.
 */
export function applyCreateActionParentOverride(
  resolvedAction: ResolvedCreateAction,
  parentEntity: CreateActionParentEntityOverride | undefined
): ResolvedCreateAction {
  if (!parentEntity) {
    return resolvedAction;
  }

  const binding = resolvedAction.action.parentBinding;
  if (!binding) {
    throw new Error(
      `Create action '${resolvedAction.action.presetKey}' binds no parent entity; an explicit parent cannot be applied.`
    );
  }

  if (binding.entityTypeKey !== parentEntity.entityTypeKey) {
    throw new Error(
      `Create action '${resolvedAction.action.presetKey}' binds parent '${binding.alias}' of type '${binding.entityTypeKey}', not '${parentEntity.entityTypeKey}'.`
    );
  }

  const overriddenParent: CreateActionParentEntity = {
    alias: binding.alias,
    entityTypeKey: parentEntity.entityTypeKey,
    entityId: parentEntity.entityId,
    entitySlug: parentEntity.entitySlug ?? "",
  };

  return {
    ...resolvedAction,
    parentEntities: [
      ...(resolvedAction.parentEntities ?? []).filter(
        (candidate) => candidate.alias !== binding.alias
      ),
      overriddenParent,
    ],
  };
}

/**
 * Caller-supplied attribute values win over the action's compiled `defaultValues`: the popover
 * asked for this exact value (a dataset's `stage`, say) while the defaults are only a starting
 * point. Applied before the parent binding so a binding target field is still filled in.
 */
export function mergeCreateActionPayloadValues(
  defaults: Record<string, unknown>,
  payloadValues: Record<string, unknown> | undefined
): Record<string, unknown> {
  if (!payloadValues) {
    return defaults;
  }

  return { ...defaults, ...payloadValues };
}

/**
 * The explicitly chosen folder is where the entity lands, so it is also the folder segment of the
 * path we navigate to — never the folder the create was triggered from.
 */
export function applyExplicitProjectTreeFolderScope(
  scope: FolderRouteScope,
  folder: CreateActionProjectTreeFolderOverride | undefined
): FolderRouteScope {
  if (!folder) {
    return scope;
  }

  return { ...scope, folderSlugs: [folder.folderSlug] };
}

/**
 * Only types that actually bind `projectTreeFolder` may carry the id in their create payload —
 * `projectEntities.create` rejects payload keys with no matching binding, so a nested type (field,
 * dashboard visual, …) takes its folder from its parent and keeps the id out of the payload.
 */
export function resolveExplicitProjectTreeFolderId(input: {
  structure: Pick<CompiledFrameworkStructure, "topologyRules">;
  targetEntityTypeKey: string;
  folder: CreateActionProjectTreeFolderOverride;
}) {
  return targetSupportsProjectTreeFolderBinding(input.structure, input.targetEntityTypeKey)
    ? input.folder.projectTreeFolderId
    : undefined;
}

async function resolveProjectTreeFolderIdFromReturnPath(input: {
  convex: ReturnType<typeof useConvex>;
  projectId: Id<"projects">;
  structure: Pick<CompiledFrameworkStructure, "rootFolders" | "folderNodes" | "topologyRules">;
  returnPath: string;
  targetEntityTypeKey: string;
  folderOverride?: CreateActionProjectTreeFolderOverride;
}) {
  if (input.folderOverride) {
    return resolveExplicitProjectTreeFolderId({
      structure: input.structure,
      targetEntityTypeKey: input.targetEntityTypeKey,
      folder: input.folderOverride,
    });
  }

  const folderSlug = resolveProjectTreeFolderSlugFromReturnPath({
    structure: input.structure,
    returnPath: input.returnPath,
    targetEntityTypeKey: input.targetEntityTypeKey,
  });
  if (!folderSlug) {
    return undefined;
  }

  const folder = await input.convex.query(api.projectTreeFolders.getBySlug, {
    projectId: input.projectId,
    slug: folderSlug,
  });
  if (!folder) {
    throw new Error(`Project tree folder "${folderSlug}" not found`);
  }

  return folder._id;
}

export function applyParentBindingDefaults(
  defaults: Record<string, unknown>,
  resolvedAction: ResolvedCreateAction
) {
  const binding = resolvedAction.action.parentBinding;
  if (binding) {
    if (!binding.targetField) {
      // The payload field is the only mechanism that persists the parent edge:
      // projectEntities.create syncs entityRelations from catalog bindings with a
      // createBinding.targetField. A binding without one would create the entity outside its
      // parent's collection, so refuse loudly instead of silently misplacing it.
      throw new Error(
        `Create action '${resolvedAction.action.presetKey}' binds parent '${binding.alias}' without a targetField; set createBinding.targetField on the '${binding.entityTypeKey}' → '${resolvedAction.action.targetEntityTypeKey}' topology rule.`
      );
    }

    const parent = resolvedAction.parentEntities?.find(
      (candidate) => candidate.alias === binding.alias
    );
    if (!parent) {
      throw new Error(`Missing required create parent '${binding.alias}'`);
    }

    if (defaults[binding.targetField] === undefined) {
      defaults[binding.targetField] = parent.entityId;
    }
  }
}

function buildOwnerCreateArgs(input: {
  entityTypeKey: string;
  catalog: ProjectFrameworkCatalog | null | undefined;
  defaults: Record<string, unknown>;
  currentUserId: Id<"users">;
}) {
  const ownerFields: Record<string, Id<"users">> = {};

  for (const field of getCatalogOwnerFields(input.catalog, input.entityTypeKey)) {
    const ownerId = normalizeOwnerId(input.defaults[field.persistenceKey]);
    const fallbackOwnerId = field.defaultOnCreate === "actor" ? input.currentUserId : undefined;
    const resolvedOwnerId = ownerId ?? fallbackOwnerId;
    if (resolvedOwnerId) {
      ownerFields[field.persistenceKey] = resolvedOwnerId;
    }
  }

  return ownerFields;
}

export function useCreateEntityFromAction(projectId: Id<"projects">) {
  const convex = useConvex();
  const currentUser = useCurrentUser();
  const aliases = useFrameworkEntityAliases(projectId);
  const structure = useProjectFrameworkStructure(projectId);
  const entityDisplayMap = useMemo(() => buildEntityDisplayMap(structure ?? null), [structure]);

  const createProjectEntity = useObservedMutation(api.projectEntities.create, {
    feature: "project-entities",
    operation: "create",
    includeRequestId: true,
  });
  const upsertDraft = useObservedMutation(api.versions.upsertDraft, {
    feature: "versions",
    operation: "upsert-draft",
    includeRequestId: true,
    // The initial draft only pre-opens the name editor; the create below deliberately swallows
    // its rejection, so mark the outcome per the observability contract for non-blocking work.
    failureOutcome: "best_effort_failure",
  });

  return useCallback(
    async (input: {
      resolvedAction: ResolvedCreateAction;
      rootFolderSlug: string;
      returnPath: string;
      surface?: AnalyticsSurface;
      createdVia?: "sidebar_create" | "collection_create" | "entity_context_create";
      /**
       * Create into this project-tree folder instead of the one inferred from `returnPath`, and
       * return a path inside it. Ignored for the payload when the target type has no
       * `projectTreeFolder` binding (nested types inherit their parent's folder), but still used
       * for the returned path so the caller lands where it expects.
       */
      projectTreeFolder?: CreateActionProjectTreeFolderOverride;
      /**
       * Bind the action's `parentBinding` to this entity instead of the one the navigation
       * context resolved.
       */
      parentEntity?: CreateActionParentEntityOverride;
      /** Initial attribute values; they win over the action's compiled `defaultValues`. */
      payloadValues?: Record<string, unknown>;
    }) => {
      if (!structure) {
        throw new Error("Framework structure is unavailable for create navigation.");
      }

      if (!aliases) {
        throw new Error("Framework aliases are unavailable for create navigation.");
      }

      if (!currentUser?._id) {
        throw new Error("Current user not available");
      }

      const { rootFolderSlug, returnPath } = input;
      const resolvedAction = applyCreateActionParentOverride(
        input.resolvedAction,
        input.parentEntity
      );
      const analyticsSurface = input.surface ?? "collection_page";
      const createdVia = input.createdVia ?? "collection_create";
      trackEntityEvent("entity_create_action_selected", {
        surface: analyticsSurface,
        project_id: projectId,
        source_node_key: resolvedAction.sourceNodeKey,
        target_entity_type_key: resolvedAction.action.targetEntityTypeKey,
        preset_key: resolvedAction.action.presetKey,
      });
      const parentScopeValues = buildCreateParentScopeValues(resolvedAction.parentEntities);
      const defaults = mergeCreateActionPayloadValues(
        resolveCreateDefaults({
          defaultValues: resolvedAction.action.defaultValues,
          parentScopeValues,
        }),
        input.payloadValues
      );
      applyParentBindingDefaults(defaults, resolvedAction);
      const sourceNode =
        structure?.folderNodes.find((node) => node.key === resolvedAction.sourceNodeKey) ?? null;
      const entityScope = applyExplicitProjectTreeFolderScope(
        stripCollectionSlugFromFolderScope(
          resolveActionScope(returnPath),
          sourceNode?.kind === "collection" ? sourceNode.slug : null
        ),
        input.projectTreeFolder
      );

      const projectTreeFolderId = await resolveProjectTreeFolderIdFromReturnPath({
        convex,
        projectId,
        structure,
        returnPath,
        targetEntityTypeKey: resolvedAction.action.targetEntityTypeKey,
        folderOverride: input.projectTreeFolder,
      });
      const payload = buildProjectEntityCreatePayload({
        defaults,
        projectTreeFolderId,
        managedProjectTreeFolderSystemKey: getManagedProjectTreeFolderSystemKey({
          entityTypeKey: resolvedAction.action.targetEntityTypeKey,
          rootFolderSlug,
          structure,
        }),
      });
      const createdEntity = await createProjectEntity({
        projectId,
        entityTypeKey: resolvedAction.action.targetEntityTypeKey,
        // Never adopt a same-named entity: the draft write below would overwrite its draft,
        // and the adopted entity could be re-parented away from its real collection. The name
        // below is system-generated, so a collision is the system's to resolve: the backend
        // picks the next free "Untitled X N" atomically inside the create transaction.
        onDuplicate: "suffix",
        name: buildUntitledEntityName({
          action: resolvedAction.action,
          displayLabel:
            entityDisplayMap.get(resolvedAction.action.targetEntityTypeKey)?.label ?? null,
        }),
        ownerFields: buildOwnerCreateArgs({
          entityTypeKey: resolvedAction.action.targetEntityTypeKey,
          catalog: structure.compiledCatalog,
          defaults,
          currentUserId: currentUser._id,
        }),
        payload,
      });
      if (!createdEntity) {
        throw new Error(`Created ${resolvedAction.action.targetEntityTypeKey} entity not found`);
      }
      const entityId = String(createdEntity._id);
      const entitySlug = createdEntity.slug;
      trackEntityEvent("entity_created", {
        surface: analyticsSurface,
        project_id: projectId,
        entity_type_key: resolvedAction.action.targetEntityTypeKey,
        entity_id: entityId,
        source_node_key: resolvedAction.sourceNodeKey,
        created_via: createdVia,
      });

      // No separate relation write: projectEntities.create persists the parent binding and
      // writes the entityRelations edge in the same mutation, so entity and placement are
      // atomic — a failure or closed tab after this point can never leave a misplaced entity.

      // The initial draft is an enhancement (it pre-opens the name editor via __createMeta),
      // not part of the entity's integrity: the editor materializes drafts on demand. A draft
      // failure is already reported by the observed mutation, so degrade gracefully instead of
      // failing a create that has fully succeeded.
      try {
        await upsertDraft({
          projectId,
          entityTypeKey: resolvedAction.action.targetEntityTypeKey,
          entityId,
          createdBy: currentUser._id,
          summary: "Draft",
          changes: [],
          snapshotJson: buildInitialDraftSnapshot({
            targetEntityTypeKey: resolvedAction.action.targetEntityTypeKey,
            catalog: structure.compiledCatalog,
            defaults,
            createdEntity: createdEntity as Record<string, unknown>,
            returnPath,
            sourceNodeKey: resolvedAction.sourceNodeKey,
          }),
        });
      } catch {
        // Reported by useObservedMutation; the created entity is complete and navigable.
      }

      return buildEntityPath({
        kind: "entity",
        entityTypeKey: resolvedAction.action.targetEntityTypeKey,
        entityAlias: getRequiredEntityAlias(aliases, resolvedAction.action.targetEntityTypeKey),
        entitySlug,
        scope: {
          ...entityScope,
          rootFolderSlug,
          entityLineage: [],
        },
      });
    },
    [
      aliases,
      createProjectEntity,
      convex,
      currentUser?._id,
      entityDisplayMap,
      projectId,
      structure,
      upsertDraft,
    ]
  );
}
